import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const UserFormModal = ({ isOpen, onClose, onSave, user, mode, language }) => {
  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    userType: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user) {
      setFormData({
        name: user?.name || '',
        phoneNumber: user?.phoneNumber || '',
        userType: user?.userType || ''
      });
    } else {
      setFormData({ name: '', phoneNumber: '', userType: '' });
    }
    setErrors({});
  }, [user, isOpen]);

  const labels = {
    addTitle: { en: 'Add New User', ar: 'إضافة مستخدم جديد' },
    editTitle: { en: 'Edit User', ar: 'تعديل المستخدم' },
    name: { en: 'Name', ar: 'الاسم' },
    phoneNumber: { en: 'Phone Number', ar: 'رقم الهاتف' },
    userType: { en: 'User Type', ar: 'نوع المستخدم' },
    cancel: { en: 'Cancel', ar: 'إلغاء' },
    save: { en: 'Save', ar: 'حفظ' },
    required: { en: 'This field is required', ar: 'هذا الحقل مطلوب' },
    invalidPhone: { en: 'Invalid phone number format', ar: 'تنسيق رقم الهاتف غير صحيح' }
  };

  const userTypeOptions = [
    { value: 'Client', label: language === 'en' ? 'Client' : 'عميل' },
    { value: 'Workshop', label: language === 'en' ? 'Workshop' : 'ورشة' },
    { value: 'Agency', label: language === 'en' ? 'Agency' : 'وكالة' },
    { value: 'Emergency Provider', label: language === 'en' ? 'Emergency Provider' : 'مزود طوارئ' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = labels?.required?.[language];
    }

    if (!formData?.phoneNumber?.trim()) {
      newErrors.phoneNumber = labels?.required?.[language];
    } else if (!/^[+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,9}$/?.test(formData?.phoneNumber)) {
      newErrors.phoneNumber = labels?.invalidPhone?.[language];
    }

    if (!formData?.userType) {
      newErrors.userType = labels?.required?.[language];
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-4 border border-border w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">
            {mode === 'add' ? labels?.addTitle?.[language] : labels?.editTitle?.[language]}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth"
            aria-label="Close"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Input
            label={labels?.name?.[language]}
            value={formData?.name}
            onChange={(e) => handleChange('name', e?.target?.value)}
            error={errors?.name}
            required
          />

          <Input
            label={labels?.phoneNumber?.[language]}
            value={formData?.phoneNumber}
            onChange={(e) => handleChange('phoneNumber', e?.target?.value)}
            error={errors?.phoneNumber}
            required
          />

          <Select
            label={labels?.userType?.[language]}
            options={userTypeOptions}
            value={formData?.userType}
            onChange={(value) => handleChange('userType', value)}
            error={errors?.userType}
            required
          />

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={onClose}
            >
              {labels?.cancel?.[language]}
            </Button>
            <Button
              type="submit"
              variant="default"
              fullWidth
            >
              {labels?.save?.[language]}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserFormModal;